import { useState, useEffect, useCallback } from 'react';
import { auditService, AuditLogEntry, AuditLogFilters, AuditLogsResponse } from '../services/audit.service';

interface UseAuditLogsReturn {
  logs: AuditLogEntry[];
  loading: boolean;
  error: Error | null;
  meta: AuditLogsResponse['meta'] | null;
  filters: AuditLogFilters;
  setFilters: (filters: AuditLogFilters) => void;
  refetch: () => void;
}

export const useAuditLogs = (initialFilters: AuditLogFilters = {}): UseAuditLogsReturn => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [meta, setMeta] = useState<AuditLogsResponse['meta'] | null>(null);
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    limit: 20,
    ...initialFilters,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await auditService.getAuditLogs(filters);
      setLogs(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch audit logs'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    logs,
    loading,
    error,
    meta,
    filters,
    setFilters,
    refetch: fetchData,
  };
};
